import React from 'react';
import styled, { css, keyframes } from 'styled-components';

const shimmer = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const shimmerStyle = css`
  background: linear-gradient(
    90deg,
    var(--line-soft, #ece9e3) 25%,
    #f7f5f1 50%,
    var(--line-soft, #ece9e3) 75%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.4s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

export const SkeletonBar = styled.span<{ $width?: string; $height?: number }>`
  display: block;
  width: ${({ $width }) => $width ?? '100%'};
  height: ${({ $height }) => $height ?? 12}px;
  border-radius: 6px;
  ${shimmerStyle}
`;

const SkeletonCircle = styled.span<{ $size: number }>`
  display: block;
  flex: none;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  ${shimmerStyle}
`;

// Deterministic widths so rows look organic without re-rendering randomly.
const BAR_WIDTHS = ['72%', '48%', '60%', '38%', '66%', '54%'];
const barWidth = (row: number, column: number) =>
  BAR_WIDTHS[(row * 3 + column) % BAR_WIDTHS.length];

const SkeletonRegion = styled.div`
  position: relative;
`;

const SkeletonCaption = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 16px 6px;
  color: var(--ink-faint);
  font-size: 12.5px;
`;

const Spinner = styled.span<{ $size?: number }>`
  width: ${({ $size }) => $size ?? 14}px;
  height: ${({ $size }) => $size ?? 14}px;
  border-radius: 50%;
  border: 2px solid var(--line-soft, #e8e5df);
  border-top-color: var(--accent, #7a1f3d);
  animation: ${spin} 0.8s linear infinite;

  @media (prefers-reduced-motion: reduce) {
    animation-duration: 2.4s;
  }
`;

const SkeletonTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;

  th,
  td {
    padding: 14px 12px;
    text-align: left;
  }

  thead th {
    border-bottom: 1px solid var(--line-soft, #e8e5df);
  }

  tbody tr + tr td {
    border-top: 1px solid var(--line-soft, #f0ede7);
  }
`;

type TableSkeletonProps = {
  label: string;
  columns?: number;
  rows?: number;
};

// Table-level loading: keeps the card in place and shows placeholder rows, so
// only the data area reads as "loading" (as opposed to GlobalLoadingState).
export const TableSkeleton: React.FC<TableSkeletonProps> = ({
  label,
  columns = 7,
  rows = 8
}) => (
  <SkeletonRegion role="status" aria-busy="true" aria-live="polite">
    <SkeletonCaption>
      <Spinner />
      {label}
    </SkeletonCaption>
    <SkeletonTable aria-hidden="true">
      <thead>
        <tr>
          {Array.from({ length: columns }, (_, column) => (
            <th key={column}>
              <SkeletonBar $width={column === 0 ? '55%' : '70%'} $height={10} />
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: rows }, (_, row) => (
          <tr key={row}>
            {Array.from({ length: columns }, (_, column) => (
              <td key={column}>
                <SkeletonBar $width={barWidth(row, column)} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </SkeletonTable>
  </SkeletonRegion>
);

const PanelBody = styled.div`
  padding: 20px 16px 28px;
`;

const ChartBars = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 14px;
  height: 180px;
  padding: 16px 8px 0;
`;

const ChartBar = styled.span<{ $height: number }>`
  flex: 1;
  height: ${({ $height }) => $height}%;
  border-radius: 6px 6px 0 0;
  ${shimmerStyle}
`;

const CHART_HEIGHTS = [38, 62, 48, 80, 56, 90, 44, 70];

// Chart/panel-level loading, same vocabulary as TableSkeleton.
export const ChartSkeleton: React.FC<{ label: string }> = ({ label }) => (
  <SkeletonRegion role="status" aria-busy="true" aria-live="polite">
    <PanelBody>
      <SkeletonCaption>
        <Spinner />
        {label}
      </SkeletonCaption>
      <ChartBars aria-hidden="true">
        {CHART_HEIGHTS.map((height, index) => (
          <ChartBar key={index} $height={height} />
        ))}
      </ChartBars>
    </PanelBody>
  </SkeletonRegion>
);

const GlobalWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  animation: ${keyframes`from { opacity: 0; } to { opacity: 1; }`} 0.2s ease-out;
`;

const StatusCard = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 22px 24px;
  background: #fff;
  border: 1px solid var(--line-soft, #e8e5df);
  border-radius: 14px;
`;

const StatusText = styled.div`
  flex: 1;
  min-width: 0;
`;

const StatusTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: var(--ink, #1c1b1a);
`;

const StatusSubtitle = styled.div`
  margin-top: 2px;
  font-size: 13px;
  color: var(--ink-faint);
`;

const GhostCard = styled.div`
  background: #fff;
  border: 1px solid var(--line-soft, #e8e5df);
  border-radius: 14px;
  padding: 18px 20px;
`;

const GhostRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const GhostGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 18px;
`;

type GlobalLoadingStateProps = {
  title: string;
  subtitle: string;
};

// Page-level loading: the whole view (context bar, tabs, content) is waiting on
// the prerequisite queries, so it is replaced by a ghost layout + progress.
export const GlobalLoadingState: React.FC<GlobalLoadingStateProps> = ({
  title,
  subtitle
}) => (
  <GlobalWrap role="status" aria-busy="true" aria-live="polite">
    <StatusCard>
      <Spinner $size={28} />
      <StatusText>
        <StatusTitle>{title}</StatusTitle>
        <StatusSubtitle>{subtitle}</StatusSubtitle>
      </StatusText>
    </StatusCard>
    <GhostCard aria-hidden="true">
      <GhostGrid>
        {Array.from({ length: 4 }, (_, index) => (
          <div key={index}>
            <SkeletonBar $width="40%" $height={10} />
            <div style={{ height: 10 }} />
            <GhostRow>
              <SkeletonCircle $size={26} />
              <SkeletonBar $width="70%" $height={26} />
            </GhostRow>
          </div>
        ))}
      </GhostGrid>
    </GhostCard>
    <GhostCard aria-hidden="true">
      <GhostRow style={{ gap: 24, marginBottom: 6 }}>
        {[90, 130, 110, 80, 70].map((width, index) => (
          <SkeletonBar key={index} $width={`${width}px`} $height={14} />
        ))}
      </GhostRow>
      <SkeletonTable>
        <tbody>
          {Array.from({ length: 6 }, (_, row) => (
            <tr key={row}>
              {Array.from({ length: 6 }, (_, column) => (
                <td key={column}>
                  <SkeletonBar $width={barWidth(row, column)} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </SkeletonTable>
    </GhostCard>
  </GlobalWrap>
);
